import { Observable } from 'rxjs';
import { InjectionToken } from '../../injection';
import { EmptyResponse } from '../data/empty-response.do';
import { PagedResults } from '../data/paged-results.model';
import { SerializableObjectList } from '../data/serializable-object-list.model';
import { UserEmail } from '../data/user-email.do';
import { UserToken } from '../data/user-token.do';
import { User } from '../data/user.do';
import { Dictionary, ParameterValueType } from '../interfaces/dictionary.interface';

/**
 * Possible query parameters for User service.
 *  @param page Page index.
 *  @param per_page Results per page.
 *  @param filter The search string.
 */
export interface UserQueryParameters extends Dictionary<ParameterValueType> {
  page?: number;
  per_page?: number;
  filter?: string;
  adminDomainId?: string;
}

export interface IUserService {
  /**
   * Saves a new user.
   * @param user The user instance.
   */
  createUser(user: User): Observable<User>;

  /**
   * Returns an user by it's ID.
   * @param userId The user id.
   * @param disableErrorHandling can be used to disable error handling for this request.
   */
  getUser(userId: string, disableErrorHandling?: boolean): Observable<User>;

  /**
   * Updates an existing user.
   * @param user The user instance
   */
  updateUser(user: User): Observable<User>;

  /**
   * Deletes an existing user.
   * @param userId The userId
   */
  deleteUser(userId: string): Observable<EmptyResponse>;

  /**
   * Returns all the retrieved user for the provided query.
   * @param queryParameters Dictionary containing the query parameters.
   */
  getAll(queryParameters: UserQueryParameters): Observable<PagedResults<User>>;

  /**
   * Add email to user account.
   * @param userId The user id.
   * @param email Email to be added.
   */
  addEmail(userId: string, email: string): Observable<UserEmail>;

  /**
   * Email to be removed from user.
   * @param userId The user id.
   * @param email Email that will be removed.
   */
  removeEmail(userId: string, email: string): Observable<EmptyResponse>;

  /**
   * Returns user's authorization tokens by it's ID.
   * @param userId The user id.
   */
  getTokens(userId: string): Observable<SerializableObjectList<UserToken>>;

  /**
   * Delete user's authorization token by it's ID.
   * @param tokenId The user authorization token id.
   */
  deleteToken(tokenId: string): Observable<EmptyResponse>;
}

export const IUserService = new InjectionToken('IUserService');
